'use client';

import { useCallback, useRef } from 'react';
import { mutate, type Arguments } from 'swr';

type SWRKey = Arguments;

export interface OptimisticMutationOptions<TData, TResult> {
  /**
   * SWR cache key(s) to update optimistically.
   * Can be a single key or array of keys.
   */
  key: SWRKey | SWRKey[];

  /**
   * Function to apply the optimistic update.
   * Receives current cached data and returns the optimistically updated data.
   */
  optimisticUpdate: (current: TData | undefined) => TData;

  /**
   * The actual mutation function (API call).
   * Should throw on error to trigger rollback.
   */
  mutationFn: () => Promise<TResult>;

  /**
   * Optional callback on success.
   * Receives the mutation result.
   */
  onSuccess?: (result: TResult) => void;

  /**
   * Optional callback on error.
   * Receives the error.
   */
  onError?: (error: Error) => void;

  /**
   * Whether to revalidate after successful mutation.
   * Default: true
   */
  revalidateOnSuccess?: boolean;

  /**
   * Delay before revalidation (ms).
   * Default: 0 (immediate)
   */
  revalidateDelay?: number;
}

export interface UseOptimisticMutationReturn<TResult> {
  /**
   * Execute the mutation with optimistic update.
   */
  execute: () => Promise<TResult | undefined>;

  /**
   * Whether the mutation is currently in progress.
   */
  isPending: boolean;

  /**
   * The last error that occurred.
   */
  error: Error | null;
}

/**
 * Hook for performing optimistic mutations with automatic rollback.
 *
 * @example
 * ```tsx
 * const { execute, isPending } = useOptimisticMutation({
 *   key: ['bookings', businessId],
 *   optimisticUpdate: (current) => current?.map(b =>
 *     b.id === bookingId ? { ...b, status: 'completed' } : b
 *   ),
 *   mutationFn: () => completeBooking(bookingId),
 *   onSuccess: () => toast.success('Booking completed'),
 *   onError: (error) => toast.error(error.message),
 * });
 *
 * // In click handler:
 * await execute();
 * ```
 */
export function useOptimisticMutation<TData, TResult>(
  options: OptimisticMutationOptions<TData, TResult>
): UseOptimisticMutationReturn<TResult> {
  const isPendingRef = useRef(false);
  const errorRef = useRef<Error | null>(null);

  const execute = useCallback(async (): Promise<TResult | undefined> => {
    const {
      key,
      optimisticUpdate,
      mutationFn,
      onSuccess,
      onError,
      revalidateOnSuccess = true,
      revalidateDelay = 0,
    } = options;

    // Prevent concurrent mutations
    if (isPendingRef.current) {
      return undefined;
    }

    isPendingRef.current = true;
    errorRef.current = null;

    const keys = Array.isArray(key) ? key : [key];

    // Store original data for rollback
    const originalData = new Map<SWRKey, TData | undefined>();

    try {
      // Apply optimistic update to all keys
      for (const k of keys) {
        // Get current data (this is synchronous via SWR's cache)
        // We need to capture it before the optimistic update
        await mutate<TData>(
          k,
          (current) => {
            originalData.set(k, current);
            return optimisticUpdate(current);
          },
          { revalidate: false }
        );
      }

      // Execute the actual mutation
      const result = await mutationFn();

      // Call success callback
      onSuccess?.(result);

      // Revalidate if needed
      if (revalidateOnSuccess) {
        const revalidate = () => {
          for (const k of keys) {
            mutate(k, undefined, { revalidate: true });
          }
        };

        if (revalidateDelay > 0) {
          setTimeout(revalidate, revalidateDelay);
        } else {
          revalidate();
        }
      }

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      errorRef.current = err;

      // Rollback optimistic updates
      for (const k of keys) {
        const original = originalData.get(k);
        await mutate(k, original, { revalidate: false });
      }

      // Call error callback
      onError?.(err);

      // Re-throw to allow caller to handle
      throw err;
    } finally {
      isPendingRef.current = false;
    }
  }, [options]);

  return {
    execute,
    get isPending() {
      return isPendingRef.current;
    },
    get error() {
      return errorRef.current;
    },
  };
}

/**
 * Simpler helper for one-off optimistic mutations without the hook pattern.
 * Use when you need a quick optimistic update in an event handler.
 *
 * @example
 * ```tsx
 * const handleComplete = async () => {
 *   await optimisticMutate({
 *     key: ['bookings', businessId],
 *     optimisticData: bookings.map(b => b.id === id ? { ...b, status: 'completed' } : b),
 *     mutationFn: () => completeBooking(id),
 *     rollbackData: bookings,
 *   });
 * };
 * ```
 */
export async function optimisticMutate<TData, TResult>({
  key,
  optimisticData,
  mutationFn,
  rollbackData,
  onSuccess,
  onError,
  revalidate = true,
}: {
  key: SWRKey;
  optimisticData: TData;
  mutationFn: () => Promise<TResult>;
  rollbackData: TData;
  onSuccess?: (result: TResult) => void;
  onError?: (error: Error) => void;
  revalidate?: boolean;
}): Promise<TResult | undefined> {
  // Apply optimistic update
  await mutate(key, optimisticData, { revalidate: false });

  try {
    const result = await mutationFn();
    onSuccess?.(result);

    if (revalidate) {
      mutate(key, undefined, { revalidate: true });
    }

    return result;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));

    // Rollback
    await mutate(key, rollbackData, { revalidate: false });
    onError?.(err);

    throw err;
  }
}

/**
 * Pattern for fire-and-forget optimistic mutations.
 * Shows success immediately, fires API in background, rolls back on error.
 *
 * @example
 * ```tsx
 * const handleMarkPaid = () => {
 *   fireAndForgetMutation({
 *     key: swrKeys.upcomingBookings(businessId, 5),
 *     getOptimisticData: (current) => ({
 *       ...current,
 *       bookings: current.bookings.map(b =>
 *         b.id === bookingId ? { ...b, status: 'confirmed_paid', paid: true } : b
 *       ),
 *     }),
 *     mutationFn: () => markBookingPaid(bookingId, method),
 *     onOptimisticApplied: () => {
 *       toast.success('Payment recorded');
 *       closeSheet();
 *     },
 *     onError: (error) => toast.error(`Failed: ${error.message}`),
 *   });
 * };
 * ```
 */
export function fireAndForgetMutation<TData, TResult>({
  key,
  getOptimisticData,
  mutationFn,
  onOptimisticApplied,
  onSuccess,
  onError,
  revalidateOnSuccess = true,
}: {
  key: SWRKey | SWRKey[];
  getOptimisticData: (current: TData | undefined) => TData;
  mutationFn: () => Promise<TResult>;
  onOptimisticApplied?: () => void;
  onSuccess?: (result: TResult) => void;
  onError?: (error: Error) => void;
  revalidateOnSuccess?: boolean;
}): void {
  const keys = Array.isArray(key) ? key : [key];
  const originalData = new Map<SWRKey, TData | undefined>();

  // Apply optimistic update synchronously
  keys.forEach((k) => {
    mutate<TData>(
      k,
      (current) => {
        originalData.set(k, current);
        return getOptimisticData(current);
      },
      { revalidate: false }
    );
  });

  // Notify that optimistic update was applied
  onOptimisticApplied?.();

  // Fire API call in background
  mutationFn()
    .then((result) => {
      onSuccess?.(result);

      if (revalidateOnSuccess) {
        keys.forEach((k) => {
          mutate(k, undefined, { revalidate: true });
        });
      }
    })
    .catch((error) => {
      const err = error instanceof Error ? error : new Error(String(error));

      // Rollback all keys
      keys.forEach((k) => {
        const original = originalData.get(k);
        mutate(k, original, { revalidate: false });
      });

      onError?.(err);
    });
}
